import { ClockAfternoonIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useGetClient } from '@/api/market-shop/client/get-client'
import { useGetSettingsByToken } from '@/api/market-shop/client/get-settings-by-token'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { DAY_NAMES_NARROW, DEFAULT_TZ } from '@/constants'
import { useForceRerender } from '@/hooks'
import { CHECK_FOR_OPEN_STATUS_INTERVAL } from '@/pages/market-shop/market-shop.constants'
import { formatWeekday } from '@/utils'

import { formatMinutesToTime, getMarketShopStatus } from '../../market-shop.utils'
import * as S from './working-hours.styled'

export const WorkingHours = () => {
  const { t } = useTranslation(['market-shop'])
  const { forceRerender, tick } = useForceRerender()

  const { data, isResidentFlow } = useGetSettingsByToken()

  const { data: clientData } = useGetClient({
    enabled: isResidentFlow,
  })
  const buildingTz = clientData?.buildingId?.timezone || DEFAULT_TZ

  const statusInfo = useMemo(() => {
    return getMarketShopStatus(data, buildingTz, t)
  }, [data?.businessHours, buildingTz, tick, t])

  const hasValidWorkingHours = statusInfo && isResidentFlow

  const currentDayName = useMemo(() => {
    const now = toZonedTime(new Date(), buildingTz)
    return DAY_NAMES_NARROW[now.getDay()]
  }, [buildingTz])

  // NOTE: This effect handles a case when the shop is nearly opened/closed, user will see the moment
  // of state change without a page reload
  useEffect(() => {
    if (!hasValidWorkingHours) return

    const interval = setInterval(() => {
      forceRerender()
    }, CHECK_FOR_OPEN_STATUS_INTERVAL)

    return () => clearInterval(interval)
  }, [])

  if (!hasValidWorkingHours) return null

  return (
    <S.WorkingHours>
      <S.WorkingHoursAccordion
        $isOpenStatus={statusInfo.isOpen}
        title={
          <>
            <Icon
              icon={ClockAfternoonIcon}
              size="2.4rem"
              color={statusInfo.isOpen ? 'green500' : 'orange500'}
            />
            <Typography
              as="span"
              size="text-large"
              weight="semibold"
              color={statusInfo.isOpen ? 'green500' : 'orange500'}
            >
              {statusInfo.isOpen
                ? t('market-shop:workingHours.open')
                : t('market-shop:workingHours.closed')}
            </Typography>
            {statusInfo.nextEventText && (
              <Typography as="span" size="text-large" weight="medium" color="black500">
                {statusInfo.nextEventText}
              </Typography>
            )}
          </>
        }
      >
        {DAY_NAMES_NARROW.map((dayName) => {
          const dayHours = data?.businessHours.find((h) => h.name === dayName)
          const isCurrent = dayName === currentDayName

          return (
            <S.ScheduleDay key={dayName}>
              <Typography
                size="text-large"
                weight={isCurrent ? 'semibold' : 'medium'}
                color={isCurrent ? 'black800' : 'black500'}
              >
                {formatWeekday(dayName, 'short')}
              </Typography>
              <Typography
                size="text-large"
                weight={isCurrent ? 'semibold' : 'medium'}
                color={isCurrent ? 'black800' : 'black500'}
              >
                {dayHours
                  ? `${formatMinutesToTime(dayHours.startTime)} - ${formatMinutesToTime(
                      dayHours.endTime,
                    )}`
                  : t('market-shop:workingHours.closed')}
              </Typography>
            </S.ScheduleDay>
          )
        })}
      </S.WorkingHoursAccordion>
    </S.WorkingHours>
  )
}
